import React, { useEffect, useRef, useState } from 'react';

/* ================================================================
   HintBar — per-screen onboarding hints
   ----------------------------------------------------------------
   Usage:
     <HintBar screenId="deconstruct" hints={["…","…"]} />

   - Rotates hints every 5 s.
   - Dismiss (×) persists to localStorage under `rai.hint.{screenId}`.
   - When visible, sets `--hint-h: 36px` on <html> so screens with
     explicit content-area heights can subtract it via
     `calc(… − var(--hint-h, 0px))`.
   ================================================================ */

// v2 suffix invalidates pre-v2 dismissals so previously-dismissed hints
// appear again whenever this version bumps (e.g. after content changes).
const STORAGE_KEY = (id) => `rai.hint.v2.${id}`;
const ROTATE_MS = 5000;
const BAR_H = 36;

const HintBar = ({ screenId, hints = [] }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY(screenId)) === 'dismissed'; }
    catch { return false; }
  });
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  // Rotate hints
  useEffect(() => {
    if (dismissed || hints.length < 2) return undefined;
    timerRef.current = setInterval(
      () => setIdx((i) => (i + 1) % hints.length),
      ROTATE_MS
    );
    return () => clearInterval(timerRef.current);
  }, [dismissed, hints.length]);

  // Publish --hint-h so screens with fixed content-area heights
  // can shrink their grid to keep the status bar on-screen.
  useEffect(() => {
    if (dismissed) return undefined;
    document.documentElement.style.setProperty('--hint-h', `${BAR_H}px`);
    return () => document.documentElement.style.removeProperty('--hint-h');
  }, [dismissed]);

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY(screenId), 'dismissed'); }
    catch { /* noop */ }
    setDismissed(true);
  };

  if (dismissed || !hints.length) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        height: BAR_H,
        background: 'rgba(0,212,200,0.06)',
        borderBottom: '1px solid rgba(0,212,200,0.15)',
        padding: '0 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'relative',
        zIndex: 2,
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          color: 'var(--teal)',
          fontSize: 11,
          textShadow: '0 0 6px rgba(0,212,200,0.6)',
        }}
      >
        ◈
      </span>

      <span
        style={{
          flex: 1,
          fontFamily: 'var(--fs)',
          fontSize: 12,
          color: 'var(--t2)',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {hints[idx]}
      </span>

      {hints.length > 1 && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} aria-hidden="true">
          {hints.map((_, i) => (
            <span
              key={i}
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: i === idx ? 'var(--teal)' : 'rgba(0,212,200,0.3)',
                transition: 'background 200ms ease-out',
              }}
            />
          ))}
        </div>
      )}

      <button
        onClick={handleDismiss}
        aria-label="Dismiss hint"
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          padding: 0,
          background: 'transparent',
          border: '1px solid rgba(0,212,200,0.3)',
          borderRadius: '50%',
          color: 'var(--teal)',
          cursor: 'pointer',
          fontSize: 14,
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--fm)',
        }}
      >
        ×
      </button>
    </div>
  );
};

export default HintBar;
